<script>
	// NPM Modules
	import { createEventDispatcher, tick } from 'svelte';
	// Components
	import Modal from '@/Components/Modal.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';
	import Switch from '@/Components/Switch.svelte';
	import PillTextInput from '@/Components/PillTextInput.svelte';
	// Services
	import { CurrencyFormat } from '@/Services/Helpers.js';
	import { notify } from '@/Services/Notify.js';
	// Constants
	import { Status as MarketStatus } from '@/Constants/Market.js';
	// Stores
	import DerivedMarketGroupStore from '@/Stores/Sport/DerivedMarketGroupStore.js';
	import MarketStore from '@/Stores/Sport/MarketStore.js';

	export let open = false;
	export let event;
	export let market;
	export let selections;

	let sending = false;
	let errors;

	let derivedMarkets = {
		derived_market_template_ids: [],
		status: MarketStatus.Open,
		selections: []
	};
	let marketsToAdd = [];
	let derivedMarketGroupSelector = null;

	$: open && getDerivedMarketGroups();
	$: selections && generateSelectionArray();

	const dispatch = createEventDispatcher();

	function generateSelectionArray() {
		for (const selection in selections) {
			let found = false;
			for (const derivedSelection in derivedMarkets.selections) {
				if (derivedMarkets.selections[derivedSelection].id === selections[selection].id) {
					derivedMarkets.selections[derivedSelection].name;
					derivedMarkets.selections[derivedSelection].price;
					derivedMarkets.selections[derivedSelection].override_price;
					found = true;
				}
			}
			if (!found) {
				derivedMarkets.selections.push({
					id: selections[selection].id,
					name: selections[selection].name,
					price: selections[selection].price,
					override_price: selections[selection].override_price,
					include: true
				});
			}
		}
	}

	const close = (created) => {
		open = false;
		if (created) {
			dispatch('created');
			return false;
		}
		dispatch('close');
	};

	function getDerivedMarketGroups() {
		DerivedMarketGroupStore.getDerivedMarketGroupsForSport(event.sport.id);
	}

	function removeMarketFromAddList(market) {
		let indexToRemove;
		for (const index in marketsToAdd) {
			if (marketsToAdd[index].id === market.id) {
				indexToRemove = index;
				break;
			}
		}
		marketsToAdd.splice(indexToRemove, 1);
		for (const index in derivedMarkets.derived_market_template_ids) {
			if (derivedMarkets.derived_market_template_ids[index] === market.id) {
				indexToRemove = index;
				break;
			}
		}
		derivedMarkets.derived_market_template_ids.splice(indexToRemove, 1);
		marketsToAdd = marketsToAdd;
		derivedMarkets.derived_market_template_ids = derivedMarkets.derived_market_template_ids;
	}

	function createDerivedMarkets() {
		if (derivedMarkets.derived_market_template_ids.length == 0) {
			notify({
				type: 'warning',
				title: 'Error!',
				message: 'You must add at least 1 market template to be derived.',
				timeout: 3
			});
			return false;
		}
		sending = true;
		MarketStore.createDerivedMarkets(market, derivedMarkets)
			.then(() => {
				notify({
					type: 'success',
					title: 'Derived markets successfully created',
					message: 'Derived markets successfully created!',
					timeout: 3
				});
				MarketStore.getMarketWithSelections(market.id);
				close(true);
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Problem creating derived markets',
					timeout: 3
				});
			})
			.finally(() => (sending = false));
	}
</script>

{#if open}
	<Modal on:close={close} className="md:w-4/5 lg:w-2/3 mx-2 md:mx-auto">
		<h2 slot="header" class="pt-2 text-xl font-bold text-center">Create derived markets from "{market.name}"</h2>
		<div class="flex flex-col w-full px-4 pt-2">
			<SelectInput
				label="Derived market template"
				className="py-4 w-1/3 text-left"
				errors={errors && errors.status_id}
				name="derivedMarketGroupId"
				disabled={sending}
				required={true}
				bind:value={derivedMarketGroupSelector}
				on:change={(event) => {
					const derivedMarketGroup = $DerivedMarketGroupStore.firstWhere('id', event.target.value);
					if (
						derivedMarketGroup &&
						derivedMarketGroup.derived_market_templates &&
						derivedMarketGroup.derived_market_templates.data
					) {
						for (const derivedMarketTemplate of derivedMarketGroup.derived_market_templates.data) {
							if (!derivedMarkets.derived_market_template_ids.includes(derivedMarketTemplate.id)) {
								marketsToAdd.push(derivedMarketTemplate);
								derivedMarkets.derived_market_template_ids.push(derivedMarketTemplate.id);
								marketsToAdd = marketsToAdd; //trick reactivity
								derivedMarkets.derived_market_template_ids = derivedMarkets.derived_market_template_ids; //trick reactivity
							}
						}
					}
					tick().then(() => {
						derivedMarketGroupSelector = null;
					});
				}}
			>
				<option value={null} selected disabled>Please select group</option>
				{#each $DerivedMarketGroupStore as derivedMarketGroup}
					{#if derivedMarketGroup.market_types.includes(market.type.id.toString()) || derivedMarketGroup.market_types.includes('all')}
						<option value={derivedMarketGroup.id}>{derivedMarketGroup.name}</option>
					{/if}
				{/each}
			</SelectInput>
			<h1>Derived markets being created</h1>
			<div class="flex justify-center mt-2 flex-wrap">
				{#each marketsToAdd as market (market.id)}
					<PillTextInput
						action="remove"
						name={market.id}
						disabled={sending}
						bgColour="bg-indigo-500"
						className="m-2"
						label={market.name}
						value={false}
						on:remove={() => removeMarketFromAddList(market)}
					/>
				{:else}
					<div class="font-semibold my-2">No derived markets added yet</div>
				{/each}
			</div>
			<SelectInput
				label="Derived Market initial status (Default: Open)"
				className="py-4 text-left w-1/3"
				errors={errors && errors.status_id}
				name="marketInitialStatus"
				disabled={sending}
				required={true}
				bind:value={derivedMarkets.status}
			>
				<option value={MarketStatus.Open}>Open</option>
				<option value={MarketStatus.Suspended}>Suspended</option>
			</SelectInput>
			<div class="flex flex-row items-center gap-x-4">
				<table class="w-full">
					<thead>
						<tr class="text-left border-b border-gray-400">
							<th>Selection</th>
							<th>Price</th>
							<th>Include</th>
						</tr>
					</thead>
					{#each derivedMarkets.selections as selection (selection.id)}
						<tbody>
							<tr>
								<td>{selection.name} </td>
								<td
									>{CurrencyFormat(
										selection.override_price ? selection.override_price : selection.price,
										false,
										false
									)}</td
								>
								<td>
									<Switch name={`${selection.id}-include`} className="ml-1" bind:checked={selection.include} />
								</td>
							</tr>
						</tbody>
					{/each}
				</table>
			</div>

			<LoadingButton on:click={() => createDerivedMarkets()} loading={sending} className="my-4 btn btn-primary">
				{sending ? 'Creating' : 'Create derived market'}
			</LoadingButton>
			<button class="mb-4 btn btn-muted" on:click={() => (open = false)}>Cancel</button>
		</div>
	</Modal>
{/if}
